<template>
  <v-card class="card-selection mx-auto" width="375" flat>
    <BackButton v-if="!singlePaymentMethod" @click="$emit('abort')"/>
    <v-form v-model="formValid">
      <div v-if="hasAliases" class="card-item-container">
        <span class="pageTitle">{{$t('select_card')}}</span>
        <v-list flat class="pb-0 my-5">
            <v-list-item v-for="(alias, index) in cards.aliasList" :key="alias.aliadId"
              class="cardItem"
              :class="{selected: index === selectedAliasIndex}"
              :disabled="isDeleted(alias)"
              @click="selectedAliasIndex = index">
              <v-list-item-content>
                <div>
                  <div v-if="alias.aliasLabel" class="card-alias-label">{{ alias.aliasLabel }} </div>
                  <div v-if="!isDeleted(alias)" class="d-flex align-center">
                    <img v-if="alias.cardBrand === 'VISA'" class="mr-1" src="@/assets/Visa.svg"/>
                    <img v-else-if="alias.cardBrand === 'MC'" class="mr-1" src="@/assets/Mastercard.svg"/>
                    <img v-else class="mr-1" src="@/assets/CB.svg"/>
                    <div class="ml-2 card-number">{{ alias.cardMaskedPan }} </div>
                    <div class="ml-2 mr-1 expiry">{{ $t('valid_until') }}{{' '}}{{ getExpirationDate(alias) }} </div>
                    <v-spacer/>
                    <check-icon class='check' v-if="index === selectedAliasIndex"/>
                    <cross-icon class='cross'
                      @click.stop="promptSuppressCard(alias)"/>
                  </div> 
                  <div v-else class="d-flex align-center">
                    <img class="mr-1 deleted-card-icon" src="@/assets/CB.svg"/>
                    <div class="ml-2 deleted-card">{{ $t('deleted_card') }} </div>
                  </div>              
                </div>        
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              class="cardItem mb-1"            
              :class="{selected: cards.aliasList.length === selectedAliasIndex}"
              @click="selectedAliasIndex = cards.aliasList.length">
              <v-list-item-content class="py-0">
                <div class="py-2 use-other-card">
                  {{$t('use_other_card')}}
                  <div class="d-flex">
                    <div>
                      <check-box
                        class="mt-0 ml-n1"
                        v-model="registerNewCard"
                        hide-details dense
                        :disabled="forceRegisterNewCard">
                        <template v-slot:label>
                          <div>
                            <div>{{ $t('save_card') }}</div>
                            <div class="mt-8 ml-n6" v-if="registerNewCard">{{ $t('save_card_add_label') }} :</div>
                          </div>
                          </template>
                        </check-box>
                        <div  v-if="registerNewCard"
                          @click.stop=""
                          @mousedown.stop="" 
                          @touchstart.stop="">
                          <input-field class="mt-n4"
                            v-model="newCardLabel"
                            :label="''"
                            :rules ='rules.aliasLabel'
                            :disabled="!registerNewCard"
                            :showRequirementLabels="false"/>
                      </div>
                    </div>
                    <check-icon class='check ml-3' v-if="cards.aliasList.length === selectedAliasIndex"/>

                  </div>
                </div> 
              </v-list-item-content>

            </v-list-item>
        </v-list>

      </div>
      <div v-else>
        <span class="pageTitle">{{$t('pay_by_card')}}</span>
        <check-box
          class="mt-8"
          v-model="registerNewCard"
          :disabled="forceRegisterNewCard"
          hide-details dense>
          <template v-slot:label>
            <div>
              <div>{{ $t('save_card') }}</div>
              <div class="mt-8 ml-n7" v-if="registerNewCard">{{ $t('save_card_add_label2') }} :</div>
          </div>
          </template>
        </check-box>
        <div v-if="registerNewCard"
            @click.stop=""
            @mousedown.stop="" 
            @touchstart.stop="">
          <input-field 
            class="mt-n5"
            v-model="newCardLabel"
            :label="''"
            :rules ='rules.aliasLabel'
            :showRequirementLabels="false"/>
        </div>
      </div>
    </v-form>

    <v-card-actions class="mt-auto pa-0 pt-10">
      <ConfirmButton v-if="useContinue"
        @click="submit"
        :disabled="!canContinue">
         {{$t('continue')}}
      </ConfirmButton>
      <ConfirmButton v-else class="icon-button"
        @click="submit"
        :disabled="!canSubmit">
          <div>{{$t('pay')}}</div>
          <img class="button-icon" src="@/assets/lock-closed-outline.svg"/>
      </ConfirmButton>
    </v-card-actions>

    <v-dialog
      v-model="showDeleteCardPrompt"
      persistent
    >      
      <v-card class="delete-card-prompt">
        <div class="delete-title">{{$t('delete_card')}}</div>
        <div class="d-flex flex-column align-center card-to-delete">          
          <div v-if="cardToDelete.aliasLabel" class="card-alias-label">
            {{cardToDelete.aliasLabel}}
          </div>
          <div class="d-flex">
            <img class="mr-1" src="@/assets/CB.svg"/>
            <div class="ml-2 card-number">{{ cardToDelete.cardMaskedPan }} </div>
            <div class="mx-1 expiry">{{ $t('valid_until') }}{{' '}}{{ getExpirationDate(cardToDelete) }} </div>
          </div>
        </div>
        <ConfirmButton
          @click="suppressCard(cardToDelete)">
          {{$t('confirm')}}
        </ConfirmButton>
        <div @click="showDeleteCardPrompt = false" class="abort-button">{{$t('abort')}}</div>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
  import { VCard, VCardActions, VSpacer, VList, VListItem, VListItemContent, VDialog, VForm } from 'vuetify/lib'
  import ConfirmButton from '@/components/ConfirmButton'
  import BackButton from '@/components/BackButton'
  import CheckBox from '@/components/CheckBox'
  import RequestHandler from '../../../mixins'
  import CheckIcon from 'vue-material-design-icons/Check.vue'
  import CrossIcon from 'vue-material-design-icons/Close.vue'
  import InputField from '@/components/InputField.vue'
  import validationRules from '@/views/payment/mixins/validationRules'
  import {mapState} from 'vuex'

  export default {
    name: 'CardSelection',
    props: {
      cards: {
        type: Object,
        default: () => {}
      },    
      mkpData: {
        type: Object,
        default: () => {}
      }
    },
    components: { 
      VCard,
      VCardActions,
      VSpacer,
      BackButton, 
      ConfirmButton,
      CheckBox,
      VList,
      VListItem,
      VListItemContent,
      CheckIcon,
      CrossIcon,
      VDialog,
      InputField,
      VForm
    },
    mixins: [RequestHandler, validationRules],
    created: function () {
      // skip card selection screen and go to thirdparty form immediately 
      if (this.cards.aliasStep === '0') this.$emit('select', {item: this.cards, registerAlias: false})
      this.prefillAlias()
    },
    computed: {
      ...mapState(['singlePaymentMethod']),
      useContinue: function () {
        return !this.cards.aliasList || this.cards.aliasList.length === this.selectedAliasIndex
      },
      canContinue () {
        return !this.registerNewCard || this.formValid
      },
      canSubmit () {
        return this.selectedAliasIndex >= 0
      },
      selectedAlias: function () {
        return this.cards.aliasList ? this.cards.aliasList[this.selectedAliasIndex] : null
      },
      hasAliases: function () {
        return this.cards.aliasList && this.cards.aliasList.length
      }
    },
    methods: {
      submit: function () {
        if (this.selectedAlias) this.$emit('select', {item: this.cards, aliasId: this.selectedAlias.aliasId, doNotAddDetails: true})
        else if (this.useContinue) this.$emit('select', {item: this.cards, registerAlias: this.registerNewCard, aliasLabel: this.registerNewCard ? this.newCardLabel : ''})
      },
      promptSuppressCard: function (card) {
        this.cardToDelete = card
        this.showDeleteCardPrompt = true
      },
      suppressCard: function (card) {
        this.showDeleteCardPrompt = false
        this.callSuppressAlias(card.aliasId)
        .then(rep => {
          if (!rep) throw new Error(this.$t('error_treatment'))
          const content = rep.content
          if (content && content.statusCode === '0') {
            this.deletedCards.push(card.aliasId)
            if (this.selectedAlias === card) this.selectedAliasIndex = -1
          }
          else if (process.env.NODE_ENV !== 'production') {
            if (this.selectedAlias === card) this.selectedAliasIndex = -1
            this.deletedCards.push(card.aliasId)
          }
        })
      },
      isDeleted: function (card) {
        return this.deletedCards.indexOf(card.aliasId) !== -1
      },
      getExpirationDate: function (alias) {
        let raw = alias.cardExpirationDate
        if (!raw || raw.length < 4) return raw
        return [raw.slice(2,4), raw.slice(0,2)].join('/')
      },
      prefillAlias () {
        let mkpData = this.mkpData
        if (!mkpData) return
        if (mkpData.registerAlias === '1') {
          this.forceRegisterNewCard = true
          this.registerNewCard = true
          if (mkpData.alias) this.newCardLabel = mkpData.alias.label
        }
    }
    },
    data: function () {
      return {
        selectedAliasIndex: 0,
        deletedCards: [],
        formValid: false,
        registerNewCard: false,
        forceRegisterNewCard: false,
        newCardLabel: '',
        cardToDelete: {},
        showDeleteCardPrompt: false
      }
    }
  }
</script>